import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbBannerUtils } from "abreact/app/banner/AbBannerUtils";
import { AbProductBranding } from "abreact/app/banner/AbProductBranding";
import { AbInlineNotification } from "abreact/feedback/AbInlineNotification";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbErrorDialog } from "abreact/incubator/ui/error/AbErrorDialog";
import { AbLoginDialogMachine } from "abreact/incubator/ui/login/AbLoginDialogMachine";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { AbPopupContext } from "abreact/incubator/ui/popup/PopupContext";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbPasswordInput } from "abreact/input/AbPasswordInput";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbVBox } from "abreact/layout/AbVBox";
import { AbImage } from "abreact/misc/AbImage";
import { AbBusyIndicator } from "abreact/progress/AbBusyIndicator";
import { AbReactServices } from "abreact/service/AbReactServices";
import { AbHBox } from "absdk/commons/ui/layout/AbHBox";
import { AbButton, AbButtonType } from "absdk/commons/ui/misc/AbButton";
import { AbWebServices } from "abweb/service/AbWebServices";
import { BrowserCheck } from "abweb/service/utils/BrowserCheck";
import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { RandomUtils } from "abweb/utils/RandomUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { action } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import portalLoginBackground from "abweb/assets/images/splash_screen.webp?resource";
const submit = async props => {
  try {
    const success = await props.machine.login();
    if (success) {
      props.popupHandle?.close();
    } else {
      props.focusOnFail();
    }
  } catch (err) {
    AbErrorDialog.displayError(err);
  }
};

// Products must have /customAssets/** in their 'ignorePatterns' security list

const calcDisplayUnsecuredCredentials = () => BrowserUtils.getLocationProtocol() === "http:" && BrowserUtils.getLocationHostname() !== "localhost" // FF also suppresses warning for localhost
&& !BrowserCheck.isFirefox();
export const AbLoginHeader = props => {
  const customLoginHeaderPath = AbBannerUtils.customBrandingPath(AbLogin.CUSTOM_LOGIN_HEADER_INFO);
  if (customLoginHeaderPath) {
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-login-header-branding"
    }, /*#__PURE__*/React.createElement("img", {
      src: customLoginHeaderPath,
      "data-test-component": "ab-image",
      alt: AbReactResources.loginDialog_customBrandingAlt()
    }));
  } else {
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-login-header-branding"
    }, /*#__PURE__*/React.createElement(AbImage, {
      image: "absdk:abinitio-logo",
      className: "ab-initio-logo",
      altText: AbReactResources.loginDialog_abInitioLogoAlt()
    }), !props.suppressProductName && /*#__PURE__*/React.createElement("div", {
      className: "ab-product-name"
    }, /*#__PURE__*/React.createElement(AbProductBranding, {
      excludeCustomerBranding: true,
      useLegalBranding: true
    })));
  }
};
export const AbBaseLoginDialog = observer(props => {
  const popupContext = React.useContext(AbPopupContext);
  const [customLoginBrandingHtml, setCustomLoginBrandingHtml] = React.useState(undefined);
  const usernameInputRef = React.useRef(null);
  const displayUnsecuredCredentialsWarning = React.useMemo(calcDisplayUnsecuredCredentials, []);
  React.useEffect(() => {
    const absPath = AbBannerUtils.customBrandingPath(AbLogin.CUSTOM_LOGIN_BANNER_INFO);
    if (!absPath) {
      setCustomLoginBrandingHtml(undefined);
    } else {
      // catch: nop - there's no custom login branding
      try {
        FetchUtils.fetchText(absPath).then(setCustomLoginBrandingHtml);
      } catch {
        setCustomLoginBrandingHtml(undefined);
      }
    }
  }, []);
  const machine = props.machine;
  const portalLoginView = Boolean(props.portalLoginView);
  const submitFunc = () => {
    submit({
      machine: machine,
      popupHandle: popupContext?.popupHandle,
      focusOnFail: () => {
        FocusUtils.setFocus(usernameInputRef.current?.inputElement);
      }
    });
  };
  const alternateImage = AbBannerUtils.customBrandingPath(AbLogin.CUSTOM_LOGIN_HEADER_INFO) !== null;
  const componentProps = ReactUtils.componentProps("ab-login-dialog", props, ["ab-login-dialog", "ab-pseudo-background", alternateImage && "ab-grayscale", machine.isBusy && "ab-login-busy", portalLoginView && "ab-portal-login"]);
  const loginFormContainer = /*#__PURE__*/React.createElement(AbHBox, null, /*#__PURE__*/React.createElement(AbVBox, {
    spacing: true,
    className: "ab-login-form-container"
  }, (portalLoginView && machine.errorMessage || !portalLoginView) && /*#__PURE__*/React.createElement(AbInlineNotification, {
    message: machine.errorMessage,
    severity: "error",
    dataTestId: "ab-login-dialog-error-message",
    className: ReactUtils.classNamesString(["ab-login-dialog-error-message", !machine.errorMessage && "ab-hidden", "ab-flexible"])
  }), props.children, /*#__PURE__*/React.createElement(AbTextInput, {
    id: "ab-login-dialog-username",
    className: "ab-login-dialog-username abweb-initial-focus",
    value: machine.username,
    disabled: props.machine.isBusy,
    onChange: action(username => machine.username = username),
    placeholder: AbReactResources.loginDialog_usernameLabel(),
    autoFocus: true,
    abRef: usernameInputRef,
    dataTestId: "username",
    __htmlProps: {
      autoComplete: "username"
    }
  }), /*#__PURE__*/React.createElement(AbPasswordInput, {
    textInputProps: {
      className: "ab-login-dialog-password",
      dataTestId: "password"
    },
    value: machine.password,
    disabled: props.machine.isBusy,
    onChange: action(password => machine.password = password),
    placeholder: AbReactResources.loginDialog_passwordLabel(),
    isCurrentPassword: true
  }), displayUnsecuredCredentialsWarning && !portalLoginView && /*#__PURE__*/React.createElement("span", {
    className: "ab-unsecured-credentials-warning"
  }, AbReactResources.loginDialog_unsecuredCredentialsWarning()), /*#__PURE__*/React.createElement(AbButton, {
    onClick: submitFunc,
    disabled: props.machine.isBusy || !props.machine.loginEnabled,
    className: "ab-dialog-ok-button",
    type: AbButtonType.SUBMIT,
    dataTestId: "ab-login-ok-button",
    label: props.submitButtonLabel ?? AbReactResources.loginDialog_logIn()
  }), props.machine.isBusy && /*#__PURE__*/React.createElement(AbBusyIndicator, {
    className: "ab-login-busy-indicator",
    message: AbReactResources.loginDialog_authProgressMessage()
  })), customLoginBrandingHtml && /*#__PURE__*/React.createElement("div", {
    className: "ab-login-customer-area",
    dangerouslySetInnerHTML: {
      __html: customLoginBrandingHtml
    }
  }));

  // swallow ESCAPE so that it does not close the login dialog
  return /*#__PURE__*/React.createElement(AbPopup, _extends({
    id: AbLogin.LOGIN_DIALOG_ID
  }, componentProps, {
    resizable: false,
    focus: false,
    onKeyDown: evt => evt.key === KeyValue.ESCAPE,
    overlay: true,
    overlayClassnames: ReactUtils.classNamesString(["ab-login-base", "ab-pseudo-background", "ab-light-mode",
    // TODO AndyA: remove this once we have a design for a dark mode login page
    alternateImage && "ab-grayscale", portalLoginView && "ab-portal-login"])
  }), /*#__PURE__*/React.createElement("div", {
    id: "ab-product-login",
    "data-test-component": "ab-login-dialog",
    className: "ab-product-login abreact-login-dialog",
    onKeyPress: e => e.key === KeyValue.ENTER && submitFunc()
  }, /*#__PURE__*/React.createElement(AbVBox, {
    spacing: true,
    className: portalLoginView ? "ab-portal-login-contents" : ""
  }, portalLoginView ? /*#__PURE__*/React.createElement("div", {
    className: "ab-portal-login-image-container"
  }, /*#__PURE__*/React.createElement(AbImage, {
    image: "absdk:portal-aidp-logo-black",
    className: "ab-portal-logo"
  })) : /*#__PURE__*/React.createElement(AbLoginHeader, {
    suppressProductName: !machine.productNameSupported
  }), portalLoginView ? /*#__PURE__*/React.createElement("div", {
    className: "ab-portal-login-form-container"
  }, loginFormContainer, displayUnsecuredCredentialsWarning && portalLoginView && /*#__PURE__*/React.createElement("span", {
    className: "ab-unsecured-credentials-warning"
  }, AbReactResources.loginDialog_unsecuredCredentialsWarning())) : loginFormContainer)));
});
export class AbLogin {
  static async login(props, successUrl) {
    const machine = new AbLoginDialogMachine(AbWebServices.sessionData.productInfo, successUrl);
    machine.errorMessage = props.errorMessage ?? null;
    machine.productNameSupported = AbReactServices.bannerProvider.productNameSupported;
    const popup = /*#__PURE__*/React.createElement(AbLoginDialog, {
      machine: machine,
      portalLoginView: props.portalLoginView
    });
    const popupHandle = PopupUtils.showPopupExplicitly(popup, {
      id: AbLogin.LOGIN_DIALOG_ID,
      layerGroupId: AbLogin.LOGIN_LAYER_GROUP_ID
    });
    await popupHandle.untilClosed;
  }

  /**
   * Note: this is used to render read-only login forms for testing.
   */
  static renderLoginForm(machine) {
    return /*#__PURE__*/React.createElement(AbLoginDialog, {
      machine: machine
    });
  }
}

/**
 * Temporary function to use an existing path to an absdk image asset to construct a path to the login backgrounds.
 */
_defineProperty(AbLogin, "LOGIN_LAYER_GROUP_ID", "login-dialog");
_defineProperty(AbLogin, "LOGIN_DIALOG_ID", "ab-login-dialog");
_defineProperty(AbLogin, "CUSTOM_LOGIN_BANNER_INFO", "customLoginPath");
_defineProperty(AbLogin, "CUSTOM_LOGIN_HEADER_INFO", "customLoginHeaderPath");
const getRandomLoginBackgroundUrl = () => {
  const logoPath = AbWebServices.getImageIdentifierManager().getIdentifierInfo("absdk:abinitio-logo").scales[0].path;
  let bgFilename = `bg_${StringUtils.prependZeroes(RandomUtils.randomWholeNumber(6).toString(), 3)}.svg`;
  return logoPath.replace(/\/images\/[^?/]+(\?[^?/]*)?$/, s => {
    const tokens = s.split("?"); // handles cache bust version
    return `/loginBackgrounds/${bgFilename}?${tokens.length === 2 ? tokens[1] : ""}`;
  });
};
const AbLoginDialog = props => {
  const portalLoginView = Boolean(props.portalLoginView);
  let backgroundUrl = portalLoginView ? portalLoginBackground : getRandomLoginBackgroundUrl();
  const logoPath = AbWebServices.getImageIdentifierManager().getIdentifierInfo("ab:abinitioLogo").scales[0].path;
  if (portalLoginView) {
    document.title = AbReactResources.loginPage_portal_title();
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("style", null, `.ab-login-base::before,
      .ab-login-dialog::before {
        background-image: url(${backgroundUrl});
        ${portalLoginView && "background-size: cover;"}
      }
      ${portalLoginView && `.ab-login-base.ab-pseudo-background.ab-portal-login::after {
        background-image: url(${logoPath});
      }`}
      `), /*#__PURE__*/React.createElement(AbBaseLoginDialog, {
    machine: props.machine,
    portalLoginView: props.portalLoginView
  }));
};
//# sourceMappingURL=AbLogin.js.map
